import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Shield, UserCog, Loader2, Crown, Flag, Lock, Unlock, Eye, AlertTriangle, Tag, ClipboardList, BookOpen, FileText, Share2, Palette } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDistanceToNow } from "date-fns";
import SymptomLibraryAdmin from "@/components/admin/SymptomLibraryAdmin";
import { AffiliateLinksAdmin } from "@/components/admin/AffiliateLinksAdmin";
import ContentReviewAdmin from "@/components/admin/ContentReviewAdmin";
import MedicalKnowledgeAdmin from "@/components/admin/MedicalKnowledgeAdmin";
import KnowledgeTopicsAdmin from "@/components/admin/KnowledgeTopicsAdmin";

interface UserProfile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  post_count: number;
  comment_count: number;
  created_at: string;
}

interface UserRole {
  id: string;
  user_id: string;
  role: 'admin' | 'moderator' | 'user';
}

interface ContentReport {
  id: string;
  reason: string;
  details: string | null;
  status: string;
  created_at: string;
  reporter_user_id: string;
  reported_post_id: string | null;
  reported_comment_id: string | null;
  reporter_profile?: {
    display_name: string | null;
  };
  reported_post?: {
    title: string;
    content: string;
    user_id: string;
  };
  reported_comment?: {
    content: string;
    user_id: string;
  };
}

interface LockedPost {
  id: string;
  title: string;
  is_locked: boolean;
  lock_reason: string | null;
  locked_at: string | null;
  created_at: string;
  user_id: string;
  profiles?: {
    display_name: string | null;
  };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [userRoles, setUserRoles] = useState<Record<string, UserRole[]>>({});
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [posts, setPosts] = useState<LockedPost[]>([]);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    if (!adminLoading) {
      if (!isAdmin) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to access this page.",
          variant: "destructive",
        });
        navigate("/");
      } else {
        fetchData();
      }
    }
  }, [isAdmin, adminLoading, navigate]);

  const fetchData = async () => {
    try {
      // Fetch all users
      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (usersError) throw usersError;

      // Fetch all roles
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("*");

      if (rolesError) throw rolesError;

      // Fetch pending reports
      const { data: reportsData, error: reportsError } = await supabase
        .from("content_reports")
        .select("*")
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (reportsError) throw reportsError;

      // Fetch reporter profiles
      const reporterIds = (reportsData || []).map(r => r.reporter_user_id);
      const { data: reporterProfiles } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", reporterIds);

      // Fetch reported posts
      const postIds = (reportsData || []).filter(r => r.reported_post_id).map(r => r.reported_post_id);
      const { data: reportedPosts } = await supabase
        .from("community_posts")
        .select("id, title, content, visible_author_id")
        .in("id", postIds);

      // Fetch reported comments
      const commentIds = (reportsData || []).filter(r => r.reported_comment_id).map(r => r.reported_comment_id);
      const { data: reportedComments } = await supabase
        .from("community_comments")
        .select("id, content, visible_author_id")
        .in("id", commentIds);

      // Combine data
      const reportsWithDetails = (reportsData || []).map(report => ({
        ...report,
        reporter_profile: reporterProfiles?.find(p => p.user_id === report.reporter_user_id),
        reported_post: (() => {
          const p: any = reportedPosts?.find((x: any) => x.id === report.reported_post_id);
          return p ? { ...p, user_id: p.visible_author_id } : undefined;
        })(),
        reported_comment: (() => {
          const c: any = reportedComments?.find((x: any) => x.id === report.reported_comment_id);
          return c ? { ...c, user_id: c.visible_author_id } : undefined;
        })()
      }));

      // Fetch all posts for lock management
      const { data: postsData, error: postsError } = await supabase
        .from("community_posts")
        .select("id, title, is_locked, lock_reason, locked_at, created_at, visible_author_id")
        .order("created_at", { ascending: false });

      if (postsError) throw postsError;

      // Fetch post author profiles (anonymous posts have no visible author)
      const postAuthorIds = (postsData || []).map((p: any) => p.visible_author_id).filter(Boolean) as string[];
      const { data: postAuthors } = postAuthorIds.length
        ? await supabase
            .from("public_profiles")
            .select("user_id, display_name")
            .in("user_id", postAuthorIds)
        : { data: [] as any[] };

      // Combine post data with profiles
      const postsWithProfiles = (postsData || []).map((post: any) => ({
        ...post,
        user_id: post.visible_author_id,
        profiles: post.visible_author_id
          ? postAuthors?.find((p: any) => p.user_id === post.visible_author_id)
          : { display_name: "Anonymous" }
      }));


      // Group roles by user_id
      const rolesByUser: Record<string, UserRole[]> = {};
      rolesData?.forEach((role) => {
        if (!rolesByUser[role.user_id]) {
          rolesByUser[role.user_id] = [];
        }
        rolesByUser[role.user_id].push(role);
      });

      setUsers(usersData || []);
      setUserRoles(rolesByUser);
      setReports(reportsWithDetails || []);
      setPosts(postsWithProfiles || []);
    } catch (error: any) {
      toast({
        title: "Error loading data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getUserRoles = (userId: string): string[] => {
    return userRoles[userId]?.map(r => r.role) || [];
  };

  const hasRole = (userId: string, role: string): boolean => {
    return getUserRoles(userId).includes(role);
  };

  const addRole = async (userId: string, role: 'admin' | 'moderator') => {
    setUpdating(userId);
    try {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role });

      if (error) throw error;

      toast({
        title: "Role assigned",
        description: `${role} role has been assigned successfully.`,
      });

      await fetchData();
    } catch (error: any) {
      toast({
        title: "Error assigning role",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  const removeRole = async (userId: string, role: string) => {
    setUpdating(userId);
    try {
      const roleRecord = userRoles[userId]?.find(r => r.role === role);
      if (!roleRecord) return;

      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("id", roleRecord.id);

      if (error) throw error;

      toast({
        title: "Role removed",
        description: `${role} role has been removed successfully.`,
      });

      await fetchData();
    } catch (error: any) {
      toast({
        title: "Error removing role",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleReport = async (reportId: string, action: 'approve' | 'dismiss') => {
    setUpdating(reportId);
    try {
      const { error } = await supabase
        .from("content_reports")
        .update({ 
          status: action === 'approve' ? 'approved' : 'dismissed',
          reviewed_at: new Date().toISOString()
        })
        .eq("id", reportId);

      if (error) throw error;

      toast({
        title: action === 'approve' ? "Report approved" : "Report dismissed",
        description: "The report has been updated.",
      });

      await fetchData();
    } catch (error: any) {
      toast({
        title: "Error updating report",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  const togglePostLock = async (postId: string, currentlyLocked: boolean) => {
    setUpdating(postId);
    try {
      const { error } = await supabase
        .from("community_posts")
        .update({
          is_locked: !currentlyLocked,
          locked_at: !currentlyLocked ? new Date().toISOString() : null,
          lock_reason: !currentlyLocked ? "Locked by moderator" : null
        })
        .eq("id", postId);

      if (error) throw error;

      toast({
        title: !currentlyLocked ? "Post locked" : "Post unlocked",
        description: !currentlyLocked ? "The post is now locked." : "The post is now unlocked.",
      });

      await fetchData();
    } catch (error: any) {
      toast({
        title: "Error updating post",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  if (adminLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back Home
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Shield className="h-10 w-10 text-primary" />
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage users, content moderation, and community reports
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/article-admin")}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" /> Article Management
              </CardTitle>
              <CardDescription>Create and edit articles</CardDescription>
            </CardHeader>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/social-media")}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="h-5 w-5" /> Social Media Manager
              </CardTitle>
              <CardDescription>Pre-written posts & sharing</CardDescription>
            </CardHeader>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/banner-generator")}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Palette className="h-5 w-5" /> Banner Generator
              </CardTitle>
              <CardDescription>Create social images</CardDescription>
            </CardHeader>
          </Card>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full grid-cols-8 max-w-6xl">
            <TabsTrigger value="users" className="gap-2">
              <UserCog className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="review" className="gap-2">
              <ClipboardList className="h-4 w-4" />
              Review
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Docs
            </TabsTrigger>
            <TabsTrigger value="topics" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Topics
            </TabsTrigger>
            <TabsTrigger value="symptoms" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Symptoms
            </TabsTrigger>
            <TabsTrigger value="affiliates" className="gap-2">
              <Tag className="h-4 w-4" />
              Affiliates
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <Flag className="h-4 w-4" />
              Reports {reports.length > 0 && <Badge variant="destructive">{reports.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="posts" className="gap-2">
              <Lock className="h-4 w-4" />
              Posts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="topics">
            <KnowledgeTopicsAdmin />
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="h-5 w-5" />
                  User Management
                </CardTitle>
                <CardDescription>
                  Assign admin or moderator roles to users. Moderators can manage community content and reports.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((user) => (
                    <div
                      key={user.user_id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <Avatar className="h-12 w-12 border-2 border-primary/20">
                          <AvatarImage src={user.avatar_url || undefined} />
                          <AvatarFallback className="bg-primary/10">
                            {user.display_name?.charAt(0).toUpperCase() || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-semibold">
                            {user.display_name || "Anonymous User"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {user.post_count} posts • {user.comment_count} comments
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {hasRole(user.user_id, 'admin') && (
                            <Badge variant="default" className="gap-1">
                              <Crown className="h-3 w-3" />
                              Admin
                            </Badge>
                          )}
                          {hasRole(user.user_id, 'moderator') && (
                            <Badge variant="secondary" className="gap-1">
                              <Shield className="h-3 w-3" />
                              Moderator
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {!hasRole(user.user_id, 'moderator') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => addRole(user.user_id, 'moderator')}
                            disabled={updating === user.user_id}
                          >
                            {updating === user.user_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>Make Moderator</>
                            )}
                          </Button>
                        )}
                        {hasRole(user.user_id, 'moderator') && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeRole(user.user_id, 'moderator')}
                            disabled={updating === user.user_id}
                          >
                            {updating === user.user_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>Remove Moderator</>
                            )}
                          </Button>
                        )}
                        {!hasRole(user.user_id, 'admin') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => addRole(user.user_id, 'admin')}
                            disabled={updating === user.user_id}
                          >
                            {updating === user.user_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>Make Admin</>
                            )}
                          </Button>
                        )}
                        {hasRole(user.user_id, 'admin') && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeRole(user.user_id, 'admin')}
                            disabled={updating === user.user_id}
                          >
                            {updating === user.user_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>Remove Admin</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="review">
            <ContentReviewAdmin />
          </TabsContent>

          <TabsContent value="knowledge">
            <MedicalKnowledgeAdmin />
          </TabsContent>

          <TabsContent value="symptoms">
            <SymptomLibraryAdmin />
          </TabsContent>


          <TabsContent value="affiliates">
            <AffiliateLinksAdmin />
          </TabsContent>

          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="h-5 w-5" />
                  Content Reports
                </CardTitle>
                <CardDescription>
                  Review and moderate reported posts and comments from the community.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No pending reports</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <div
                        key={report.id}
                        className="p-4 border rounded-lg space-y-3 bg-card"
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive">{report.reason.replace('_', ' ')}</Badge>
                              <span className="text-sm text-muted-foreground">
                                Reported {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              By: {report.reporter_profile?.display_name || "Anonymous"}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const url = report.reported_post_id 
                                  ? `/community/post/${report.reported_post_id}`
                                  : `/community`;
                                window.open(url, '_blank');
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleReport(report.id, 'approve')}
                              disabled={updating === report.id}
                            >
                              {updating === report.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Take Action"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReport(report.id, 'dismiss')}
                              disabled={updating === report.id}
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                        {report.details && (
                          <p className="text-sm bg-muted p-3 rounded">
                            <strong>Details:</strong> {report.details}
                          </p>
                        )}
                        <div className="bg-muted/50 p-3 rounded">
                          <p className="text-sm font-medium mb-1">Reported Content:</p>
                          {report.reported_post && (
                            <div>
                              <p className="text-sm font-semibold">{report.reported_post.title}</p>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {report.reported_post.content}
                              </p>
                            </div>
                          )}
                          {report.reported_comment && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {report.reported_comment.content}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="posts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Post Management
                </CardTitle>
                <CardDescription>
                  Lock or unlock posts to prevent or allow new comments.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {posts.map((post) => (
                    <div
                      key={post.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold">{post.title}</p>
                          {post.is_locked && (
                            <Badge variant="secondary" className="gap-1">
                              <Lock className="h-3 w-3" />
                              Locked
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          By: {post.profiles?.display_name || "Anonymous"} • 
                          Posted {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                        </p>
                        {post.is_locked && post.lock_reason && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Reason: {post.lock_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/community/post/${post.id}`, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={post.is_locked ? "outline" : "secondary"}
                          onClick={() => togglePostLock(post.id, post.is_locked)}
                          disabled={updating === post.id}
                          className="gap-2"
                        >
                          {updating === post.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : post.is_locked ? (
                            <>
                              <Unlock className="h-4 w-4" />
                              Unlock
                            </>
                          ) : (
                            <>
                              <Lock className="h-4 w-4" />
                              Lock
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
